import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';
import { fmtNum, fmtDate } from '../lib/util.js';

const CONFIG_LABELS = {
  steps_per_km: 'ก้าวต่อ 1 กม.',
  daily_step_goal: 'เป้าก้าว/วัน',
  daily_xp_cap: 'เพดาน XP/วัน',
  monthly_ticket_cap: 'เพดานตั๋ว/เดือน',
  phash_hamming_threshold: 'เกณฑ์รูปซ้ำ (pHash)',
  spotcheck_rate: 'อัตราสุ่มตรวจ (0–1)',
  freeze_weekly_grant: 'freeze token/สัปดาห์',
  freeze_token_cap: 'เพดาน freeze token',
};

export default function Admin({ auth }) {
  const [data, setData] = useState({ status: 'loading' });
  const [sub, setSub] = useState('overview');

  const load = useCallback(async () => {
    try {
      const res = await api('adminOverview', auth);
      setData({ status: 'ready', res });
    } catch (e) {
      setData({ status: 'error', error: String(e.message || e) });
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  if (data.status === 'loading') return <main className="app center"><p className="hint">กำลังโหลด…</p></main>;
  if (data.status === 'error') return <main className="app center"><p className="err">{data.error}</p></main>;

  return (
    <main className="app">
      <header className="topbar"><div><p className="hi">⚙️ แอดมิน</p><p className="branch">จัดการ Nose Tea Move</p></div></header>

      <div className="subtabs">
        {[['overview', 'ภาพรวม'], ['review', 'ตรวจสอบ'], ['config', 'ตั้งค่า'], ['season', 'ซีซั่น']].map(([k, label]) => (
          <button key={k} className={sub === k ? 'active' : ''} onClick={() => setSub(k)}>{label}</button>
        ))}
      </div>

      {sub === 'overview' && <Overview auth={auth} res={data.res} reload={load} />}
      {sub === 'review' && <Review auth={auth} />}
      {sub === 'config' && <Config auth={auth} config={data.res.config} reload={load} />}
      {sub === 'season' && <Season auth={auth} season={data.res.season} reload={load} />}
    </main>
  );
}

// ── ภาพรวม + รายชื่อผู้เล่น (แก้สาขาได้) ──
function Overview({ auth, res, reload }) {
  const { players, playerCount, totalKm, pending } = res;

  async function saveBranch(employeeId, branch) {
    try { await api('setBranch', { ...auth, employeeId, branch }); } catch (e) { alert(e.message); }
  }

  return (
    <>
      <section className="stat-row">
        <div className="stat"><img src="/assets/ui/portrait.png" alt="" /><b>{playerCount}</b><span>ผู้เล่น</span></div>
        <div className="stat"><img src="/assets/ui/map.png" alt="" /><b>{totalKm}</b><span>กม.รวม</span></div>
        <div className="stat"><img src="/assets/ui/chest.png" alt="" /><b>{pending}</b><span>รอตรวจ</span></div>
      </section>
      <section className="card">
        <h3 className="card-title">ผู้เล่น ({playerCount})</h3>
        <table className="atable">
          <thead><tr><th>ชื่อ</th><th>สาขา</th><th>XP</th><th>🔥</th><th>🎟️</th></tr></thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id}>
                <td>{p.display_name}</td>
                <td>
                  <input className="branch-in" defaultValue={p.branch || ''} placeholder="—"
                    onBlur={(e) => e.target.value !== (p.branch || '') && saveBranch(p.id, e.target.value)} />
                </td>
                <td>{fmtNum(p.total_xp)}</td>
                <td>{p.current_streak}</td>
                <td>{p.tickets}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

// ── คิวรีวิว (activity ที่ถูก flag/สุ่ม) ──
function Review({ auth }) {
  const [q, setQ] = useState({ status: 'loading' });

  const load = useCallback(async () => {
    try { setQ({ status: 'ready', res: await api('reviewQueue', auth) }); }
    catch (e) { setQ({ status: 'error', error: String(e.message || e) }); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  async function decide(activityId, decision) {
    try { await api('reviewDecision', { ...auth, activityId, decision }); await load(); }
    catch (e) { alert(e.message); }
  }

  if (q.status === 'loading') return <p className="hint">กำลังโหลด…</p>;
  if (q.status === 'error') return <p className="err">{q.error}</p>;
  const queue = q.res.queue || [];

  return (
    <section className="card">
      <h3 className="card-title">คิวรีวิว ({queue.length})</h3>
      {queue.length === 0 && <p className="hint">ไม่มีรายการรอตรวจ 🎉</p>}
      {queue.map((a) => {
        let flags = [];
        try { flags = JSON.parse(a.risk_flags || '[]'); } catch { /* ignore */ }
        return (
          <div key={a.id} className="review-item">
            <div className="ri-head">
              <b>{a.display_name}</b>
              <span>{fmtDate(a.activity_date)} · {fmtNum(a.steps)} ก้าว</span>
            </div>
            <div className="flags">{flags.map((f) => <span key={f} className="flag">{f}</span>)}</div>
            <div className="ri-actions">
              <button className="btn ghost sm" onClick={() => decide(a.id, 'confirm')}>ผ่าน</button>
              <button className="btn danger sm" onClick={() => decide(a.id, 'reject')}>ไม่ผ่าน</button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ── ตั้งค่า (จูนตัวเลข §8) ──
function Config({ auth, config, reload }) {
  const [saved, setSaved] = useState('');

  async function save(key, value) {
    try {
      await api('setConfig', { ...auth, key, value });
      setSaved(key); setTimeout(() => setSaved(''), 1500);
      reload();
    } catch (e) { alert(e.message); }
  }

  return (
    <section className="card">
      <h3 className="card-title">ตั้งค่าระบบ (มีผลทันที ไม่ต้อง deploy)</h3>
      {Object.entries(CONFIG_LABELS).map(([key, label]) => (
        <label key={key} className="cfg-row">
          <span>{label}{saved === key && <em className="saved"> ✓ บันทึกแล้ว</em>}</span>
          <input type="number" step="any" defaultValue={config[key]}
            onBlur={(e) => Number(e.target.value) !== Number(config[key]) && save(key, e.target.value)} />
        </label>
      ))}
      <p className="hint">แก้แล้วคลิกที่อื่น (blur) = บันทึกอัตโนมัติ</p>
    </section>
  );
}

// ── ซีซั่น + จับฉลาก ──
function Season({ auth, season, reload }) {
  const [newName, setNewName] = useState('');
  const [winners, setWinners] = useState(1);
  const [draw, setDraw] = useState(null);
  const [busy, setBusy] = useState(false);

  async function act(fn) { setBusy(true); try { await fn(); } catch (e) { alert(e.message); } finally { setBusy(false); } }

  return (
    <>
      <section className="card">
        <h3 className="card-title">ซีซั่นปัจจุบัน</h3>
        {season
          ? <p>{season.name} · จบ {season.ends_at || '—'}</p>
          : <p className="hint">ยังไม่มีซีซั่นที่เปิดอยู่</p>}
        {season && (
          <button className="btn danger" disabled={busy}
            onClick={() => confirm('จบซีซั่นนี้?') && act(async () => { await api('seasonEnd', auth); reload(); })}>
            จบซีซั่นนี้
          </button>
        )}
      </section>

      <section className="card">
        <h3 className="card-title">เริ่มซีซั่นใหม่</h3>
        <input className="field-in" placeholder="ชื่อซีซั่น เช่น ซีซั่น 2: ลงใต้"
          value={newName} onChange={(e) => setNewName(e.target.value)} />
        <button className="btn" disabled={busy || !newName}
          onClick={() => act(async () => { await api('seasonStart', { ...auth, name: newName }); setNewName(''); reload(); })}>
          เริ่มซีซั่นใหม่
        </button>
        <p className="hint">⚠️ ต้องเพิ่มหมุดเส้นทาง (journey_route) ของซีซั่นใหม่ใน D1 เองก่อนจะโชว์แผนที่</p>
      </section>

      <section className="card">
        <h3 className="card-title">🎲 จับฉลาก (ตามจำนวนตั๋ว)</h3>
        <label className="cfg-row"><span>จำนวนผู้ชนะ</span>
          <input type="number" min="1" max="10" value={winners} onChange={(e) => setWinners(e.target.value)} />
        </label>
        <button className="btn" disabled={busy}
          onClick={() => act(async () => setDraw(await api('drawTickets', { ...auth, winners: Number(winners) })))}>
          จับฉลากเลย!
        </button>
        {draw && (
          <div className="draw-result">
            <p className="hint">ตั๋วในซีซั่น: {draw.pool} ใบ</p>
            {draw.winners.length === 0
              ? <p className="hint">ยังไม่มีตั๋วในซีซั่นนี้</p>
              : <ol className="winners">{draw.winners.map((w, i) => <li key={i}>🏆 {w.display_name}{w.branch ? ` (${w.branch})` : ''}</li>)}</ol>}
          </div>
        )}
      </section>
    </>
  );
}
